import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowDown, ShieldCheck } from "lucide-react";
import { IMAGES, CONTACT } from "../data";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1, delay: 0.25 + i * 0.12, ease: [0.19, 1, 0.22, 1] },
  }),
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section ref={ref} data-testid="hero" className="relative h-[100svh] min-h-[640px] overflow-hidden bg-obsidian">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={IMAGES.hero} alt="Forest canopy" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/40 to-obsidian/50" />
        <div className="absolute inset-0 bg-gradient-to-r from-obsidian/70 to-transparent" />
      </motion.div>

      <div className="relative h-full max-w-[1400px] mx-auto px-5 md:px-10 flex flex-col justify-end pb-16 md:pb-20">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.15, duration: 0.8 }}
          className="flex items-center gap-3 mb-6"
        >
          <span className="h-[1px] w-10 bg-terra" />
          <span className="text-bone/80 text-xs uppercase tracking-[0.3em]">Owner-Operated · Est. Local</span>
        </motion.div>

        <h1 className="font-display font-black text-bone tracking-tighter leading-[0.86] text-[15vw] sm:text-[12vw] lg:text-[8.5rem]">
          {["Tree removal", "done the", "right way."].map((t, i) => (
            <span key={i} className="reveal-mask">
              <motion.span custom={i} variants={line} initial="hidden" animate="show" className="block">
                {i === 2 ? (
                  <span><span className="text-terra">right</span> way.</span>
                ) : t}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.8 }}
          className="mt-8 flex flex-col md:flex-row md:items-end md:justify-between gap-8"
        >
          <p className="text-bone/85 text-base md:text-lg max-w-md leading-relaxed">
            Dependable, hands-on tree work for jobs of every size — from routine removals to
            large, difficult, and technical projects. Safety first, property protected.
          </p>
          <div className="flex items-center gap-4">
            <button
              data-testid="hero-quote-btn"
              onClick={() => scrollTo("quote")}
              className="btn-wipe bg-terra text-bone font-bold px-8 py-4 text-base tracking-tight"
            >
              Request a Free Quote
            </button>
            <button
              data-testid="hero-services-btn"
              onClick={() => scrollTo("services")}
              className="text-bone font-semibold border-b-2 border-bone/40 pb-1 hover:border-terra transition-colors"
            >
              Our Services
            </button>
          </div>
        </motion.div>
      </div>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-bone/50 flex flex-col items-center gap-2">
        <ArrowDown size={18} className="animate-bounce" />
      </div>
    </section>
  );
};
